import React, { useState } from "react";
import { Plus, HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "Does this work if we don't use social media?",
    a: "Absolutely. Whether your visitors come from Google, referrals, your bulletin, an email blast, a flyer, a printed program, or word of mouth — the funnel does the same job: turn high-intent visitors into booked sessions, registrations, or members. Where they came from doesn't matter. What matters is what happens when they land.",
  },
  {
    q: "What kinds of organizations does this work for?",
    a: "Any business or organization where structured bookings, sign-ups, or registrations matter — beauty pros, coaches, studios, schools, equestrian academies, churches and faith communities, wellness practitioners, camps and retreats, trainers, non-profits, and dozens of other owner-operated niches. If your visitors need to choose, register, or book — this works.",
  },
  {
    q: "Can this work with my current booking or registration system?",
    a: "Yes. We integrate with whatever you're already using — Acuity, Calendly, Square Appointments, Vagaro, Mindbody, GoHighLevel, Eventbrite, Tithe.ly, Stripe, or your church/school management system. If you don't have one yet, we'll recommend the simplest one for your situation and set it up.",
  },
  {
    q: "What if I don't have a website yet?",
    a: "Perfect — we build from scratch. You'll get a domain, hosting, mobile-first design, and a booking funnel built around your services. No piecing tools together yourself.",
  },
  {
    q: "Is this just a website or something more?",
    a: "It's a structured funnel, not just a pretty page. Every section is engineered to move a visitor from \"interested\" to \"signed up\" — clear services, clear pricing or info, trust elements, social proof, and a frictionless booking step. It's the system around your scheduling tool.",
  },
  {
    q: "How long does it take to launch?",
    a: "From 24 hours (Same-Day Sprint) to about 2 weeks (Standard). Most owner-operators choose the 7-day Fast-Track. We work in days, not months.",
  },
  {
    q: "Do you write the copy and pick the photos?",
    a: "Yes. You give us your services and any existing brand assets. We write the copy, source any imagery you don't have, and design the whole funnel. You review and approve before launch.",
  },
  {
    q: "What happens after launch?",
    a: "Your monthly plan kicks in — we keep the site fast, secure, and continuously optimized for bookings. You can request edits, add new services, swap photos, or test new offers any time. We handle the technical side.",
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="rounded-2xl border border-nav-800 bg-nav-900/60 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left"
      >
        <span className="font-display font-bold text-base sm:text-lg text-white">
          {item.q}
        </span>
        <span
          className={`grid place-items-center h-8 w-8 rounded-full bg-brand/15 border border-brand/30 text-brand shrink-0 transition-transform ${
            open ? "rotate-45" : ""
          }`}
          aria-hidden="true"
        >
          <Plus className="h-4 w-4" />
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 sm:px-6 pb-5 text-sm text-nav-300 leading-relaxed">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="section bg-nav-950"
    >
      <div className="container-x">
        <div className="max-w-3xl mx-auto text-center">
          <span className="eyebrow justify-center">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            Common questions
          </span>
          <h2 className="mt-4 h-display text-4xl sm:text-5xl">
            <HelpCircle className="inline-block h-9 w-9 text-brand mr-2 align-middle" />
            Honest answers.
          </h2>
          <p className="mt-4 text-lg text-nav-300">
            What other owner-operators ask before getting started.
          </p>
        </div>

        <div className="mt-12 max-w-3xl mx-auto space-y-3">
          {FAQS.map((item, i) => (
            <FaqItem key={item.q} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
